import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Select,
    MenuItem,
    Grid,
    Button,
    TextField,
    IconButton,
    Paper,
    Snackbar,
    Alert,
    FormControl,
    InputLabel,
    Divider,
    useTheme,
    useMediaQuery,
    Card,
    CardContent,
    Tooltip, Dialog, DialogTitle, DialogContent, DialogActions
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import BookIcon from "@mui/icons-material/Book";
//import axios from "axios";
import api from "../axios/axiosInstance";
import useSanitizationGuard from "../hooks/useSanitizationGuard";
import {
    School as SchoolIcon,
    Psychology as PsychologyIcon,
    Dashboard as DashboardIcon,
    LibraryBooks as BooksIcon,
    ErrorOutline as ErrorOutlineIcon
} from "@mui/icons-material";


const UnitManagerTabs = () => {
    const [subjects, setSubjects] = useState([]);
    const [selectedSubject, setSelectedSubject] = useState("");
    const [units, setUnits] = useState([]);
    const [unitName, setUnitName] = useState("");
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
    const [validationDialog, setValidationDialog] = useState({ open: false, message: "" });

    // 🔒 Sanitization guard
    const { check, WarningDialog } = useSanitizationGuard();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    useEffect(() => {
        api.get(`/grades/subjects/`)
            .then(res => setSubjects(res.data))
            .catch(err => {
                // Error handled silently
            });
    }, []);

    useEffect(() => {
        if (!selectedSubject) {
            setUnits([]);
            return;
        }
        api.get(`/grades/units/`, {
            params: { subject_id: selectedSubject }
        })
            .then(res => setUnits(res.data))
            .catch(err => {
                // Error handled silently
            });
    }, [selectedSubject]);

    const handleAddUnit = async () => {
        if (!selectedSubject) {
            setSnackbar({ open: true, message: "Please select a subject before adding a unit.", severity: "warning" });
            return;
        }
        if (!unitName.trim()) {
            setSnackbar({ open: true, message: "Unit name cannot be empty.", severity: "warning" });
            return;
        }
        // 🔒 Block if risky characters/patterns are detected
        if (check(unitName, "Unit Name").blocked) return;
        try {
            await api.post(`/grades/units/create-name-only/`, {
                subject_id: selectedSubject,
                unit_name: unitName.trim()
            });

            setSnackbar({ open: true, message: "✅ Unit added successfully.", severity: "success" });
            setUnitName("");

            const res = await api.get(`/grades/units/`, {
                params: { subject_id: selectedSubject }
            });
            setUnits(res.data);
        } catch (err) {
            const errorData = err.response?.data;

            // Check for specific unit duplicate error
            if (errorData?.unit_duplicate) {
                setValidationDialog({ open: true, message: errorData.unit_duplicate });
                return;
            }

            let message = "Failed to add unit.";

            if (Array.isArray(errorData)) {
                message = errorData[0];
            } else if (typeof errorData === "object" && errorData !== null) {
                const firstKey = Object.keys(errorData)[0];
                const value = errorData[firstKey];
                if (Array.isArray(value)) {
                    message = value[0];
                } else if (typeof value === "string") {
                    message = value;
                }
            } else if (typeof errorData === "string") {
                message = errorData;
            }
            setValidationDialog({ open: true, message });
        }
    };

    const handleDeleteUnit = async (unitId) => {
        try {
            await api.delete(`/grades/units/${unitId}/delete/`);
            setSnackbar({ open: true, message: "🗑️ Unit deleted.", severity: "info" });
            setUnits(prev => prev.filter(u => u.id !== unitId));
        } catch (err) {

            setSnackbar({ open: true, message: "Failed to delete unit.", severity: "error" });
        }
    };

    return (
        <Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: 1000, mx: "auto", backgroundColor: theme.palette.background.default, borderRadius: 3 }}>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    mb: 2,
                }}
            >
                <Box sx={{ width: 6, height: 36, bgcolor: "primary.main", borderRadius: 1 }} />
                <Typography variant="h5" fontWeight={700} color="text.primary" sx={{ fontSize: '1rem' }}>
                    Create a new Unit
                </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 2.5, mb: 2, fontSize: '.9rem' }}>
                Select a Subject <BooksIcon />, provide a unit name, and Click +Add Unit to save.
            </Typography>

            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Select Subject <BooksIcon /> </InputLabel>
                <Select
                    value={selectedSubject}
                    onChange={(e) => setSelectedSubject(e.target.value)}
                    label="Select Subject"
                >
                    {subjects.map((s) => (
                        <MenuItem key={s.id} value={s.id}>
                            {s.subject_name} {s.class_level}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <Paper
                elevation={3}
                sx={{
                    p: { xs: 2, sm: 3, md: 4 },
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: "divider",
                    mb: 4
                }}
            >
                <Typography
                    variant="h6"
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 2,
                        fontWeight: 700,
                        fontSize: { xs: "1.1rem", sm: "1.3rem" },
                        color: theme.palette.primary.main
                    }}
                >
                    <BookIcon />
                    Add New Unit
                </Typography>

                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={9}>
                        <TextField
                            label="Unit Name"
                            value={unitName}
                            onChange={(e) => setUnitName(e.target.value)}
                            fullWidth
                            placeholder="Enter unit name (e.g., Fractions, Forces)"
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <Button
                            variant="contained"
                            onClick={handleAddUnit}
                            fullWidth
                            startIcon={<AddIcon />}
                            sx={{
                                py: 1.5,
                                borderRadius: 2,
                                fontWeight: 600,
                                boxShadow: 2,
                                '&:hover': {
                                    transform: 'translateY(-1px)',
                                    boxShadow: 4
                                }
                            }}
                        >
                            Add Unit
                        </Button>
                    </Grid>
                </Grid>

                {units.length > 0 && (
                    <Box sx={{ mt: 4 }}>
                        <Typography
                            variant="h6"
                            sx={{
                                mb: 2,
                                fontSize: { xs: '1rem', sm: '1.2rem' },
                                fontWeight: 600,
                                color: theme.palette.text.primary
                            }}
                        >
                            Existing Units ({units.length})
                        </Typography>

                        <Grid container spacing={2}>
                            {units.map((unit) => (
                                <Grid item xs={12} sm={6} md={4} key={unit.id}>
                                    <Card
                                        elevation={2}
                                        sx={{
                                            height: "100%",
                                            transition: "all 0.3s ease",
                                            '&:hover': {
                                                transform: 'translateY(-2px)',
                                                boxShadow: 4
                                            }
                                        }}
                                    >
                                        <CardContent
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                px: 2,
                                                py: 1.5
                                            }}
                                        >
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                <BookIcon color="primary" fontSize="small" />
                                                <Typography
                                                    variant="subtitle1"
                                                    fontWeight="bold"
                                                    sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}
                                                >
                                                    {unit.unit_name}
                                                </Typography>
                                            </Box>

                                            <Tooltip title="Delete Unit">
                                                <IconButton
                                                    onClick={() => handleDeleteUnit(unit.id)}
                                                    color="error"
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: "error.50",
                                                        '&:hover': {
                                                            backgroundColor: "error.100"
                                                        }
                                                    }}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                )}
            </Paper>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* Error Dialog - Themed to match ManageSkillsTab */}
            <Dialog
                open={validationDialog.open}
                onClose={() => setValidationDialog({ open: false, message: "" })}
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        minWidth: 400
                    }
                }}
            >
                <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
                    <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                        <ErrorOutlineIcon sx={{ fontSize: 48, color: 'error.main' }} />
                        <Typography variant="h6" fontWeight="bold" color="error.main">
                            Attention Required
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ textAlign: 'center', pt: 0 }}>
                    <Typography variant="body1" color="text.secondary">
                        {validationDialog.message}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
                    <Button
                        onClick={() => setValidationDialog({ open: false, message: "" })}
                        variant="contained"
                        color="primary"
                        sx={{ minWidth: 100, borderRadius: 2 }}
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
            {/* 🔒 Sanitization warning dialog (auto-controlled by the hook) */}
            <WarningDialog />
        </Box>
    );
};

export default UnitManagerTabs;
